/**
 * El afordance de instalación (ADR-015).
 *
 * Se muestra **una vez**, justo después de agregar el primer lugar, y una
 * sola vez más al tercer día distinto de uso. Nunca es un banner
 * persistente: `docs/03` §6.3 lo prohíbe, y con razón —un botón que
 * siempre está deja de verse a la semana.
 *
 * **El copy tiene un solo argumento y es a propósito.** El original
 * prometía que el mapa *"funciona hasta sin señal"*, que ADR-006 retiró
 * del MVP. El hueco no se rellena con "es más rápida" ni "como una app de
 * verdad": son cosas que no se pueden sostener, y la credibilidad es lo
 * que este producto está construyendo.
 *
 * **iOS no tiene la API.** Safari nunca dispara `beforeinstallprompt`, así
 * que ahí el botón abre instrucciones. Sin esa rama, en la plataforma del
 * owner el botón no haría absolutamente nada.
 */

import { useEffect, useState } from 'react';

import { trackEvent } from '../../shared/lib/api.js';
import { Boton, Texto } from '../../shared/ui/primitives.js';
import { esIOS, marcarOfrecido, marcarRespaldo, type MomentoDeInstalacion } from './install.js';
import { useInstallPrompt } from './useInstallPrompt.js';

export interface InstallCardProps {
  momento: MomentoDeInstalacion;
  /** El lugar que acaba de agregar. La personalización es el argumento. */
  nombreDelLugar?: string | undefined;
  /** Se llama al aceptar, rechazar o instalar: la tarjeta desaparece. */
  onCerrar: () => void;
}

export function InstallCard({ momento, nombreDelLugar, onCerrar }: InstallCardProps) {
  const { puedeInstalarNativo, instalar } = useInstallPrompt();
  const [verInstrucciones, setVerInstrucciones] = useState(false);
  const ios = esIOS();

  // Se marca al MOSTRARSE, no al responder. Alguien que cierra la app sin
  // contestar ya vio su momento; volver a pedírselo en la siguiente
  // apertura es justo lo que ADR-015 evita.
  useEffect(() => {
    if (momento === 'principal') marcarOfrecido();
    else marcarRespaldo();

    void trackEvent('install_prompt_shown');
  }, [momento]);

  async function pedirInstalacion() {
    if (ios && !puedeInstalarNativo) {
      setVerInstrucciones(true);
      return;
    }

    const salida = await instalar();
    if (salida === 'accepted') void trackEvent('app_installed');
    onCerrar();
  }

  return (
    <section
      aria-labelledby="instalar-titulo"
      style={{
        background: 'var(--gu-surface-raised)',
        border: '1px solid var(--gu-border-subtle)',
        borderRadius: 'var(--gu-radius-lg)',
        padding: 'var(--gu-space-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--gu-space-3)',
      }}
    >
      <h2
        id="instalar-titulo"
        style={{
          margin: 0,
          fontSize: 'var(--gu-text-h3)',
          lineHeight: 'var(--gu-leading-tight)',
          color: 'var(--gu-text-primary)',
        }}
      >
        {momento === 'respaldo'
          ? 'Ya vienes seguido. Ponla en tu pantalla.'
          : nombreDelLugar
            ? `Ya tienes ${nombreDelLugar} en el mapa. Ponlo a un toque.`
            : 'Ya tienes tu primer lugar. Ponlo a un toque.'}
      </h2>

      <Texto variante="secundario">
        Gourmet Urbano en tu pantalla de inicio, sin buscarlo en el navegador.
      </Texto>

      {verInstrucciones ? (
        /*
         * La única vía en iOS. Se enseñan los pasos con las palabras
         * exactas que usa Safari: "Compartir" y "Añadir a pantalla de
         * inicio" son las etiquetas literales, y parafrasearlas obligaría
         * a buscar un botón que no se llama así.
         */
        <ol
          style={{
            margin: 0,
            paddingLeft: 'var(--gu-space-5)',
            color: 'var(--gu-text-primary)',
            lineHeight: 'var(--gu-leading-relaxed)',
          }}
        >
          <li>
            Toca <strong>Compartir</strong> abajo en Safari (el cuadrito con la
            flecha hacia arriba).
          </li>
          <li>
            Baja y elige <strong>Añadir a pantalla de inicio</strong>.
          </li>
          <li>
            Toca <strong>Añadir</strong>.
          </li>
        </ol>
      ) : (
        <Boton onClick={() => void pedirInstalacion()}>Ponerla en mi pantalla</Boton>
      )}

      <Boton variante="secundario" onClick={onCerrar}>
        {verInstrucciones ? 'Listo' : 'Ahora no'}
      </Boton>
    </section>
  );
}
